/**
 * Brand Color Mapping Utility
 * Provides consistent color coding for brands across all modules
 */

// Primary brand colors as specified
const BRAND_COLORS = {
  'MH Cockpit': {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    gradient: 'bg-gradient-to-r from-red-100 to-red-50'
  },
  'MH Cognition': {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    gradient: 'bg-gradient-to-r from-blue-100 to-blue-50'
  },
  'MH Intellect': {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
    gradient: 'bg-gradient-to-r from-purple-100 to-purple-50'
  },
  'MH AgroTech': {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    gradient: 'bg-gradient-to-r from-green-100 to-green-50'
  },
  'MH Connekt': {
    bg: 'bg-pink-100',
    text: 'text-pink-700',
    border: 'border-pink-200',
    gradient: 'bg-gradient-to-r from-pink-100 to-pink-50'
  },
  'MH Lead': {
    bg: 'bg-lime-100',
    text: 'text-lime-700',
    border: 'border-lime-200',
    gradient: 'bg-gradient-to-r from-lime-100 to-lime-50'
  },
  'MHC Global': {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    gradient: 'bg-gradient-to-r from-amber-100 to-amber-50'
  },
  // Additional brands with distinct colors
  'Vels': {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
    gradient: 'bg-gradient-to-r from-cyan-100 to-cyan-50'
  },
  'Crescent': {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    gradient: 'bg-gradient-to-r from-emerald-100 to-emerald-50'
  },
  'Bharath': {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    gradient: 'bg-gradient-to-r from-orange-100 to-orange-50'
  },
  'Alliance': {
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    gradient: 'bg-gradient-to-r from-indigo-100 to-indigo-50'
  },
  'Bishop Heber': {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-200',
    gradient: 'bg-gradient-to-r from-rose-100 to-rose-50'
  },
  'Jamal': {
    bg: 'bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-200',
    gradient: 'bg-gradient-to-r from-teal-100 to-teal-50'
  },
  'Kalasalingam': {
    bg: 'bg-fuchsia-100',
    text: 'text-fuchsia-700',
    border: 'border-fuchsia-200',
    gradient: 'bg-gradient-to-r from-fuchsia-100 to-fuchsia-50'
  },
  'Sanskriti': {
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    border: 'border-violet-200',
    gradient: 'bg-gradient-to-r from-violet-100 to-violet-50'
  },
  'Sasuri': {
    bg: 'bg-sky-100',
    text: 'text-sky-700',
    border: 'border-sky-200',
    gradient: 'bg-gradient-to-r from-sky-100 to-sky-50'
  },
  'Other': {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    gradient: 'bg-gradient-to-r from-slate-100 to-slate-50'
  }
};

// Default color for unknown brands
const DEFAULT_BRAND_COLOR = {
  bg: 'bg-gray-100',
  text: 'text-gray-700',
  border: 'border-gray-200',
  gradient: 'bg-gradient-to-r from-gray-100 to-gray-50'
};

/**
 * Get brand color classes for a given brand name
 * @param {string} brand - Brand name
 * @returns {object} - Object with bg, text, border, gradient classes
 */
export const getBrandColor = (brand) => {
  return BRAND_COLORS[brand] || DEFAULT_BRAND_COLOR;
};

/**
 * Get combined classes for brand badge (standard style)
 * @param {string} brand - Brand name
 * @returns {string} - Combined className string
 */
export const getBrandBadgeClasses = (brand) => {
  const colors = getBrandColor(brand);
  return `${colors.bg} ${colors.text} ${colors.border}`;
};

/**
 * Get combined classes for brand badge with gradient (enhanced style)
 * @param {string} brand - Brand name
 * @returns {string} - Combined className string
 */
export const getBrandGradientClasses = (brand) => {
  const colors = getBrandColor(brand);
  return `${colors.gradient} ${colors.text} ${colors.border}`;
};

export default BRAND_COLORS;
